import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authService, { LoginCredentials } from '../services/authService';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';

// Dark theme colors
const PAGE_BG = '#181A20';
const CARD_BG = '#23272F';
const TEXTBOX_BG = '#23272F';
const INPUT_TEXT = '#F3F4F6';
const BUTTON_BG = '#3B82F6';
const BUTTON_TEXT = '#FFFFFF';
const PRIMARY_COLOR = '#3B82F6';
const SECONDARY_COLOR = '#B0B3B8';
const BORDER_COLOR = '#2D2F36';
const CARD_SHADOW = '0 4px 16px 0 rgba(0,0,0,0.25)';
const CARD_RADIUS = 14;

// Get API URL from environment or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      console.log(`Attempting login via auth service...`);
      
      const credentials: LoginCredentials = {
        email,
        password
      };
      
      // Use the auth service with built-in retry logic
      const authResult = await authService.login(credentials, 2);
      
      // Store the auth result in context
      authLogin(
        authResult.access_token, 
        authResult.user_role, 
        authResult.email, 
        authResult.name, 
        authResult.publisher_id
      );
      
      console.log('Login successful, redirecting user...');
      
      // Cache-busting redirect to prevent stale page state
      const redirectParams = new URLSearchParams({ 
        ts: Date.now().toString() 
      }).toString();
      
      // Redirect based on user role
      switch (authResult.user_role) {
        case 'advertiser':
          navigate(`/campaigns?${redirectParams}`);
          break;
        case 'publisher':
          navigate(`/publisher-dashboard?${redirectParams}`);
          break;
        default:
          navigate(`/?${redirectParams}`);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      
      if (err.response) {
        // Handle specific error status codes
        if (err.response.status === 401) {
          setError('Invalid email or password. Please try again.');
        } else if (err.response.status === 429) {
          setError('Too many login attempts. Please try again later.');
        } else {
          setError(`Login failed: ${err.response.data?.detail || err.message || 'Unknown error'}`);
        }
      } else if (err.request) {
        // The request was made but no response was received
        setError(`Server connection issue. Please check your internet connection and try again.`);
      } else {
        // Something else caused the error
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: PAGE_BG,
      p: 2
    }}>
      <Paper sx={{ 
        p: 4, 
        maxWidth: 400,
        width: '100%',
        bgcolor: CARD_BG,
        boxShadow: CARD_SHADOW,
        borderRadius: CARD_RADIUS,
        border: `1px solid ${BORDER_COLOR}`,
      }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            color: BUTTON_TEXT, 
            fontWeight: 700,
            textAlign: 'center',
            mb: 4
          }}
        >
          Sign In
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, bgcolor: '#2B1A1A', color: '#F87171' }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ 
              mb: 3,
              '& .MuiOutlinedInput-root': {
                color: INPUT_TEXT,
                bgcolor: TEXTBOX_BG,
                '& fieldset': {
                  borderColor: BORDER_COLOR,
                },
                '&:hover fieldset': {
                  borderColor: PRIMARY_COLOR,
                },
                '&.Mui-focused fieldset': {
                  borderColor: PRIMARY_COLOR,
                },
              },
              '& .MuiInputLabel-root': {
                color: SECONDARY_COLOR,
              },
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ 
              mb: 4,
              '& .MuiOutlinedInput-root': {
                color: INPUT_TEXT,
                bgcolor: TEXTBOX_BG,
                '& fieldset': {
                  borderColor: BORDER_COLOR,
                },
                '&:hover fieldset': {
                  borderColor: PRIMARY_COLOR,
                },
                '&.Mui-focused fieldset': {
                  borderColor: PRIMARY_COLOR,
                },
              },
              '& .MuiInputLabel-root': {
                color: SECONDARY_COLOR,
              },
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: BUTTON_BG,
              color: BUTTON_TEXT,
              fontWeight: 700,
              borderRadius: 2,
              py: 1.5,
              boxShadow: '0 2px 8px 0 rgba(59,130,246,0.10)',
              '&:hover': { 
                bgcolor: '#2563EB',
                boxShadow: '0 4px 12px 0 rgba(59,130,246,0.15)'
              },
              '&:active': { 
                bgcolor: '#1D4ED8',
                boxShadow: 'none'
              },
              '&.Mui-disabled': {
                bgcolor: BORDER_COLOR,
                color: SECONDARY_COLOR,
                opacity: 0.5,
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default LoginForm; 